import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Invoice } from '@/db/models/Billing';

/**
 * invoiceNumberRecovery — rebuild this device's invoice sequence from the
 * invoices it can see.
 *
 * ── Why this has to exist ────────────────────────────────────────────────────
 * An invoice number is `PREFIX-<slot:2><sequence:6>`. The slot is allocated by
 * the server per device per store, so two tills never collide. The sequence,
 * though, is a counter in local storage — and on web "local storage" is a thing
 * the browser is allowed to throw away. Clear site data, an eviction under
 * storage pressure, a new profile: the counter restarts at 1 and the device
 * re-issues numbers it has already used. The server has no unique constraint on
 * (store_id, invoice_number), so the duplicates are accepted silently.
 *
 * That is not a cosmetic problem. A GST tax invoice number must be unique per
 * store per financial year; duplicates corrupt GSTR-1 and the buyer's ITC claim.
 *
 * The fix is that the counter is *derivable*: every invoice this device ever
 * issued carries its slot and sequence in the number, and sync brings them back
 * down. So after each sync we take the highest sequence already spent on our own
 * slot and make sure the counter is above it.
 *
 * The counter is only ever raised, never lowered — a partially-synced device
 * may hold numbers the server has not seen yet, and those must stay spent.
 */

/** `PREFIX-<slot:2><sequence:6>` — the current, slot-qualified form. */
const SLOTTED = /-(\d{2})(\d{6})$/;

/**
 * `PREFIX-<sequence:6>` — issued before device slots existed. Such a number
 * belongs to no slot in particular, so it is counted for slot 0: that is the
 * series a single-till shop continues on, and over-reserving costs nothing
 * (numbers are cheap; collisions are not).
 */
const LEGACY = /-(\d{6})$/;

/**
 * Sequence spent by `invoiceNumber` on `slot`, or 0 if the number belongs to a
 * different slot or is not a number this scheme issued.
 */
export function sequenceForSlot(invoiceNumber: string, slot: number): number {
  const slotted = SLOTTED.exec(invoiceNumber);
  if (slotted) {
    return Number(slotted[1]) === slot ? Number(slotted[2]) : 0;
  }
  const legacy = LEGACY.exec(invoiceNumber);
  if (legacy) {
    return slot === 0 ? Number(legacy[1]) : 0;
  }
  return 0;
}

/**
 * Highest sequence already spent on `slot` across `invoiceNumbers`.
 * Returns 0 when this device has issued nothing yet.
 */
export function highestSequenceForSlot(
  invoiceNumbers: readonly string[],
  slot: number,
): number {
  let max = 0;
  for (const n of invoiceNumbers) {
    const seq = sequenceForSlot(n, slot);
    if (seq > max) max = seq;
  }
  return max;
}

/**
 * Highest sequence this device has already spent on `slot`, read from the
 * LOCAL database.
 *
 * Everything this needs is already on the device: every invoice carries its
 * slot and sequence inside its own number. That matters, because recovery used
 * to be reachable only from the tail of a successful sync — local data behind a
 * remote trigger. A till whose sync was failing therefore never recovered,
 * which is how one store ended up with five different sales all numbered
 * `LTC-00000001`.
 *
 * Deleted invoices are counted deliberately: a voided bill has still spent its
 * number, and reissuing it would be the duplicate this exists to prevent.
 */
export async function localHighestSequence(storeId: string, slot: number): Promise<number> {
  const rows = await database
    .get<Invoice>('invoices')
    .query(Q.where('store_id', storeId))
    .fetch();
  return highestSequenceForSlot(
    rows.map((r) => r.invoiceNumber).filter(Boolean),
    slot,
  );
}
